import React, { useEffect, useId, useRef, useState } from "react";
import { FieldLabel, TextInput } from "../ui/FormControls";
import { cn } from "../../lib/utils";
import {
  DEFAULT_GEOFENCE_RADIUS_M,
  MAX_GEOFENCE_RADIUS_M,
  MIN_GEOFENCE_RADIUS_M,
  clampGeofenceRadius,
  geofenceCircle,
  hasGeofenceCoords,
  readPrimaryCssColor,
  zoomForGeofenceRadius,
} from "../../lib/geofenceMap";
import {
  loadGoogleMapsCore,
  type GoogleCircleInstance,
  type GoogleMapInstance,
} from "../../lib/googleMapsPlacesLoader";

export type SiteGeofenceMapProps = {
  googleMapsApiKey?: string | null;
  lat?: number | null;
  lng?: number | null;
  defaultRadiusM?: number | null;
  radiusFieldName?: string;
  latFieldName?: string;
  lngFieldName?: string;
  includeCoordinateFields?: boolean;
  readOnly?: boolean;
};

export const SiteGeofenceMap: React.FC<SiteGeofenceMapProps> = ({
  googleMapsApiKey,
  lat,
  lng,
  defaultRadiusM,
  radiusFieldName = "job[geofence_radius_m]",
  latFieldName = "job[lat]",
  lngFieldName = "job[lng]",
  includeCoordinateFields = true,
  readOnly = false,
}) => {
  const mapId = useId();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<GoogleMapInstance | null>(null);
  const circleRef = useRef<GoogleCircleInstance | null>(null);
  const [radiusM, setRadiusM] = useState(() =>
    clampGeofenceRadius(defaultRadiusM ?? DEFAULT_GEOFENCE_RADIUS_M),
  );
  const [mapUnavailable, setMapUnavailable] = useState(false);

  const apiKey = googleMapsApiKey?.trim() || "";
  const hasCoords = hasGeofenceCoords(lat, lng);
  const showMap = Boolean(apiKey) && hasCoords && !mapUnavailable;

  useEffect(() => {
    setRadiusM(
      clampGeofenceRadius(defaultRadiusM ?? DEFAULT_GEOFENCE_RADIUS_M),
    );
  }, [defaultRadiusM]);

  useEffect(() => {
    if (!showMap || !mapRef.current || typeof window === "undefined") {
      return undefined;
    }

    let cancelled = false;
    const element = mapRef.current;
    const circle = geofenceCircle(lat as number, lng as number, radiusM);

    loadGoogleMapsCore(apiKey).then(
      (maps) => {
        if (cancelled || !element) {
          return;
        }

        const primary = readPrimaryCssColor();
        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new maps.Map(element, {
            center: circle.center,
            zoom: zoomForGeofenceRadius(circle.radius),
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          });
        } else {
          mapInstanceRef.current.setCenter(circle.center);
          mapInstanceRef.current.setZoom(zoomForGeofenceRadius(circle.radius));
        }

        if (!circleRef.current) {
          circleRef.current = new maps.Circle({
            map: mapInstanceRef.current,
            center: circle.center,
            radius: circle.radius,
            clickable: false,
            fillOpacity: 0.18,
            strokeOpacity: 0.9,
            strokeWeight: 2,
            ...(primary ? { fillColor: primary, strokeColor: primary } : {}),
          });
        } else {
          circleRef.current.setCenter(circle.center);
          circleRef.current.setRadius(circle.radius);
          if (primary) {
            circleRef.current.setOptions({
              fillColor: primary,
              strokeColor: primary,
            });
          }
        }
      },
      () => {
        if (!cancelled) {
          setMapUnavailable(true);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [apiKey, lat, lng, radiusM, showMap]);

  const radiusControl = readOnly ? (
    <p className="text-sm text-muted-foreground">
      Site area is {radiusM} meters.
    </p>
  ) : (
    <div>
      <FieldLabel htmlFor={`${mapId}-radius`}>
        Site radius ({radiusM} m)
      </FieldLabel>
      {apiKey && !mapUnavailable ? (
        <input
          id={`${mapId}-radius`}
          type="range"
          min={MIN_GEOFENCE_RADIUS_M}
          max={MAX_GEOFENCE_RADIUS_M}
          step={5}
          value={radiusM}
          disabled={!hasCoords}
          className="mt-2 w-full accent-primary disabled:cursor-not-allowed disabled:opacity-50"
          onChange={(event) =>
            setRadiusM(clampGeofenceRadius(Number(event.target.value)))
          }
        />
      ) : (
        <TextInput
          id={`${mapId}-radius`}
          type="number"
          min={MIN_GEOFENCE_RADIUS_M}
          max={MAX_GEOFENCE_RADIUS_M}
          value={radiusM}
          disabled={!hasCoords}
          className="mt-2"
          onChange={(event) =>
            setRadiusM(clampGeofenceRadius(Number(event.target.value)))
          }
        />
      )}
      <p
        className={cn(
          "mt-2 text-sm text-muted-foreground",
          !hasCoords && "opacity-50",
        )}
        aria-disabled={!hasCoords || undefined}
      >
        How close someone needs to be for the Toolkit app to match photos to
        this site. {MIN_GEOFENCE_RADIUS_M}–{MAX_GEOFENCE_RADIUS_M} meters.
      </p>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {showMap ? (
        <div
          ref={mapRef}
          role="img"
          aria-label="Job site map and geofence"
          className="h-64 w-full overflow-hidden rounded-xl border border-border bg-muted"
        />
      ) : hasCoords && !apiKey ? null : !hasCoords ? (
        <p className="text-sm text-muted-foreground">
          Pick an address to see the site on the map.
        </p>
      ) : null}
      {radiusControl}
      <input type="hidden" name={radiusFieldName} value={String(radiusM)} />
      {includeCoordinateFields && hasCoords ? (
        <>
          <input type="hidden" name={latFieldName} value={String(lat)} />
          <input type="hidden" name={lngFieldName} value={String(lng)} />
        </>
      ) : null}
    </div>
  );
};
